import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MeetingScheduler = ({ match, onClose, onSchedule }) => {
  const [meetingData, setMeetingData] = useState({
    date: '',
    time: '',
    duration: '30',
    timezone: 'America/New_York',
    notes: ''
  });
  const [isScheduling, setIsScheduling] = useState(false);

  const durationOptions = [
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Toronto', label: 'Toronto (ET)' },
    { value: 'America/Vancouver', label: 'Vancouver (PT)' }
  ];

  const handleSchedule = async () => {
    if (!meetingData?.date || !meetingData?.time) return;
    
    setIsScheduling(true);
    await onSchedule(match?.id, meetingData);
    setIsScheduling(false);
  };

  const handleChange = (field, value) => {
    setMeetingData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Icon name="Calendar" size={20} color="#06B6D4" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-[#111827]">Schedule Meeting</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
              aria-label="Close meeting scheduler"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="bg-[#F6F7FB] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-[#0025cc] to-[#0025cc] flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={24} color="#FFFFFF" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm md:text-base font-semibold text-[#111827] mb-1">{match?.title}</h4>
                <p className="text-xs md:text-sm text-[#6B7280]">{match?.industry}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Meeting Date"
              value={meetingData?.date}
              onChange={(e) => handleChange('date', e?.target?.value)}
              required
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />

            <Input
              type="time"
              label="Meeting Time"
              value={meetingData?.time}
              onChange={(e) => handleChange('time', e?.target?.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Duration"
              options={durationOptions}
              value={meetingData?.duration}
              onChange={(value) => handleChange('duration', value)}
              required
            />

            <Select
              label="Timezone"
              options={timezoneOptions}
              value={meetingData?.timezone}
              onChange={(value) => handleChange('timezone', value)}
              required
            />
          </div>

          <Input
            type="text"
            label="Meeting Notes (Optional)"
            placeholder="Add any notes or agenda items..."
            value={meetingData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
            description="These notes will be shared with the employer"
          />

          <div className="bg-emerald-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="#10B981" className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-sm md:text-base font-semibold text-emerald-900 mb-2">Meeting Confirmation</h4>
                <p className="text-xs md:text-sm text-emerald-800">
                  Both parties will receive calendar invitations and email confirmations. You can reschedule or cancel up to 24 hours before the meeting.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-[#E5E7EB] flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            size="default"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Calendar"
            iconPosition="left"
            loading={isScheduling}
            onClick={handleSchedule}
            disabled={!meetingData?.date || !meetingData?.time}
            className="flex-1"
          >
            Schedule Meeting
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MeetingScheduler;